import type { Transaction } from "@rocicorp/zero";
import { lockCommitFacets } from "./commitFacetLock";
import { zql } from "./generatedDrizzleSchema";
import { insertRows } from "./insertRows";
import { stepDirFromPath } from "./stepConfig";

type TreeFile = { path: string; blobHash: string };
type CommitContext = { tx: Transaction; commitId: string; tenantId: string };

export async function stepTreeHash(
  files: readonly TreeFile[]
): Promise<string> {
  const encoder = new TextEncoder();
  const entries = files
    .map((file) => ({ ...file, bytes: encoder.encode(file.path) }))
    .sort((a, b) => {
      for (let i = 0; i < Math.min(a.bytes.length, b.bytes.length); i++) {
        const difference = a.bytes[i]! - b.bytes[i]!;
        if (difference !== 0) return difference;
      }
      return a.bytes.length - b.bytes.length;
    })
    .map((file) => [file.path, file.blobHash]);
  const bytes = encoder.encode(JSON.stringify(["3b-step-tree", 1, entries]));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
}

async function storeStepTree(
  tx: Transaction,
  tenantId: string,
  treeHash: string,
  files: readonly TreeFile[]
) {
  // `DO UPDATE … WHERE false` locks an existing header against collection
  // without writing a no-op update into the replication stream. The header is
  // not replicated, so the client writes only the files.
  if (tx.location === "server") {
    await tx.dbTransaction.query(
      "INSERT INTO step_trees (tenant_id, hash) VALUES ($1, $2) ON CONFLICT (tenant_id, hash) DO UPDATE SET hash = EXCLUDED.hash WHERE false",
      [tenantId, treeHash]
    );
  }
  await insertRows(
    tx,
    "stepTreeFiles",
    files.map((file) => ({ ...file, treeHash, tenantId }))
  );
  return treeHash;
}

function groupStepFiles(files: readonly TreeFile[]) {
  const steps = new Map<string, TreeFile[]>();
  for (const file of files) {
    const stepDir = stepDirFromPath(file.path) ?? "";
    let step = steps.get(stepDir);
    if (!step) {
      step = [];
      steps.set(stepDir, step);
    }
    step.push({
      path: stepDir ? file.path.slice(stepDir.length + 1) : file.path,
      blobHash: file.blobHash,
    });
  }
  return steps;
}

async function insertSharedSteps(c: CommitContext, files: readonly TreeFile[]) {
  const { tx, commitId, tenantId } = c;
  const existing = new Map(
    (await tx.run(zql.commitSteps.where("commitId", commitId))).map((step) => [
      step.stepDir,
      step.treeHash,
    ])
  );
  const trees = await Promise.all(
    [...groupStepFiles(files)].map(async ([stepDir, stepFiles]) => ({
      stepDir,
      files: stepFiles,
      hash: await stepTreeHash(stepFiles),
    }))
  );
  trees.sort((a, b) => (a.hash < b.hash ? -1 : a.hash > b.hash ? 1 : 0));
  for (const tree of trees) {
    if (existing.get(tree.stepDir) === tree.hash) continue;
    const treeHash = await storeStepTree(tx, tenantId, tree.hash, tree.files);
    await tx.mutate.commitSteps.upsert({
      commitId,
      stepDir: tree.stepDir,
      treeHash,
      tenantId,
    });
  }
}

export async function insertCommitFileReferences(
  c: CommitContext,
  files: readonly TreeFile[]
) {
  await insertSharedSteps(c, files);
}

export async function snapshotCommitFiles(c: CommitContext): Promise<number> {
  const { tx, commitId, tenantId } = c;
  await lockCommitFacets(tx, commitId);
  const sharedSteps = new Set(
    (await tx.run(zql.commitSteps.where("commitId", commitId))).map(
      (step) => step.stepDir
    )
  );
  const files = (
    await tx.run(zql.commitFiles.where("commitId", commitId))
  ).filter((file) => !sharedSteps.has(stepDirFromPath(file.path) ?? ""));
  await insertSharedSteps(c, files);
  if (tx.location === "server") {
    await tx.dbTransaction.query(
      "DELETE FROM commit_files WHERE commit_id = $1 AND tenant_id = $2",
      [commitId, tenantId]
    );
  } else {
    for (const file of await tx.run(
      zql.commitFiles.where("commitId", commitId)
    ))
      await tx.mutate.commitFiles.delete({ commitId, path: file.path });
  }
  return files.length;
}

export async function copyCommitFileReferences(
  c: CommitContext,
  toCommitId: string
) {
  const { tx, commitId } = c;
  await snapshotCommitFiles(c);
  const steps = await tx.run(zql.commitSteps.where("commitId", commitId));
  await insertRows(
    tx,
    "commitSteps",
    steps.map((step) => ({ ...step, commitId: toCommitId }))
  );
}

export async function materializeCommitStep(
  tx: Transaction,
  commitId: string,
  path: string
): Promise<void> {
  await lockCommitFacets(tx, commitId);
  const stepDir = stepDirFromPath(path) ?? "";
  const step = await tx.run(
    zql.commitSteps
      .where("commitId", commitId)
      .where("stepDir", stepDir)
      .related("files")
      .one()
  );
  if (!step) return;
  await insertRows(
    tx,
    "commitFiles",
    step.files.map((file) => ({
      commitId,
      tenantId: step.tenantId,
      path: stepDir ? `${stepDir}/${file.path}` : file.path,
      blobHash: file.blobHash,
    }))
  );
  await tx.mutate.commitSteps.delete({ commitId, stepDir });
}

export async function deleteCommitFile(
  tx: Transaction,
  commitId: string,
  path: string
) {
  await materializeCommitStep(tx, commitId, path);
  await tx.mutate.commitFiles.delete({ commitId, path });
}
